import { CaretDown, LinkSimple } from '@phosphor-icons/react';
import { useTranslation } from 'react-i18next';
import { IReference, ReferenceType } from '../../common/types';
import useIsMobile from '../../hooks/use-is-mobile';
import { getExternalDocumentLink } from '../../lib/utils';
import { Button } from '../ui/button';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../ui/dropdown-menu';

interface SourcesMenuProps {
  references?: Array<IReference>;
}

function SourcesMenu(props: SourcesMenuProps) {
  const { t } = useTranslation();
  const isMobile = useIsMobile();

  if (props.references?.length) {
    const referencesWithExternalLinks = Array.from(
      new Map(props.references.map((item) => [item.sourceDocumentId, item])).values()
    ).filter((ref) => ref.externalDocumentLink.trim().length > 0);

    if (!referencesWithExternalLinks.length) {
      return null;
    }

    const count = referencesWithExternalLinks.length;
    return (
      <DropdownMenu>
        <DropdownMenuTrigger asChild>
          <Button
            className="w-full rounded-full border border-slate-200/70 bg-white text-slate-700 hover:bg-slate-50 shadow-sm"
            variant="outline"
            size={isMobile ? 'sm' : 'default'}
          >
            <LinkSimple size={16} />
            {t('common.sources')}
            <span className="ml-1 rounded-full bg-slate-100 px-2 py-0.5 text-[11px] text-slate-600">
              {count}
            </span>
            <CaretDown size={14} className="opacity-70" />
          </Button>
        </DropdownMenuTrigger>
        <DropdownMenuContent className="w-[320px] sm:w-[360px] rounded-2xl border border-slate-200/80 bg-white p-2 shadow-xl">
          <div className="px-2 py-2 text-xs font-semibold uppercase tracking-wide text-slate-500">
            {t('common.sources')}
          </div>
          {referencesWithExternalLinks.map((ref, idx) => (
            <DropdownMenuItem
              key={ref.sourceDocumentId}
              className="rounded-lg px-3 py-2 transition hover:bg-slate-50"
            >
              <a
                href={getExternalDocumentLink(ref)}
                target="_blank"
                className="text-xs sm:text-sm text-slate-700 hover:text-slate-900"
              >
                <span className="mr-2 text-[11px] text-slate-400">[{idx + 1}]</span>
                <span className="font-medium">
                  {[ReferenceType.Smpc, ReferenceType.Leaflet].includes(ref.referenceType)
                    ? t(`common.${ref.referenceType}`) + ': '
                    : ''}
                </span>
                {ref.name}
              </a>
            </DropdownMenuItem>
          ))}
        </DropdownMenuContent>
      </DropdownMenu>
    );
  }
  return null;
}

export default SourcesMenu;
